import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { fmtDateTime, useWebSocket, usePoll } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import MissionDialog from "@/components/MissionDialog";
import { Inbox, Phone, Mail, Car, Calendar, MapPin, MessageSquare, PhoneCall, UserPlus } from "lucide-react";
import { cn } from "@/lib/utils";
import { useVocab } from "@/lib/vocab";
import { useRights } from "@/lib/rights";
import { toast } from "sonner";

const BOOKING_STATUS = {
  a_traiter: { label: "À traiter", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  contacte: { label: "Contacté", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  converti: { label: "Converti", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};

export default function Demandes() {
  const voc = useVocab();
  // Secrétaire administrative (lot T5-0) : elle complète la course, sans chauffeur ; le dispatch l'attribue ensuite.
  const limited = useRights().secretaryLimited;
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMission, setDialogMission] = useState(null);
  const [dialogBookingId, setDialogBookingId] = useState(null);

  const load = useCallback(() => { api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  // Nouvelle demande /book : affichée tout de suite (temps réel), et rafraîchissement toutes les 12 s en secours.
  useWebSocket((msg) => { if (msg.type === "booking_created") load(); });
  usePoll(load);

  const setStatus = async (id, status) => {
    try { await api.patch(`/bookings/${id}/status`, { status }); toast.success("Statut mis à jour"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openAssign = async (b) => {
    if (!b.mission_id) { toast.error(`Cette demande n'a pas de créneau. Créez la ${voc.mission} depuis l'onglet ${voc.Missions}.`); return; }
    try {
      const { data } = await api.get("/missions");
      const m = data.find((x) => x.id === b.mission_id);
      if (!m) { toast.error(`${voc.Mission} liée introuvable.`); return; }
      setDialogMission(m);
      setDialogBookingId(b.id);
      setDialogOpen(true);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const onMissionSaved = async () => {
    if (dialogBookingId) {
      try { await api.patch(`/bookings/${dialogBookingId}/status`, { status: "converti" }); }
      catch (e) { /* la mission est enregistrée ; le statut se rattrapera au prochain chargement */ }
    }
    load();
  };

  const counts = bookings.reduce((a, b) => { a[b.status] = (a[b.status] || 0) + 1; return a; }, {});
  const filtered = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);
  const tabs = [["all", `Toutes (${bookings.length})`], ["a_traiter", `À traiter (${counts.a_traiter || 0})`], ["contacte", `Contacté (${counts.contacte || 0})`], ["converti", `Converti (${counts.converti || 0})`]];

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-overline text-accent">Réservations en ligne</div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-1 flex items-center gap-2"><Inbox className="h-8 w-8 text-accent" /> Demandes & prospects</h1>
      </div>

      <div className="flex flex-wrap gap-2">
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`demandes-filter-${k}`}
            className={cn("px-3.5 py-1.5 text-sm rounded-full border transition-colors", filter === k ? "bg-accent/15 border-accent/40 text-foreground" : "border-border text-muted-foreground hover:text-foreground")}>{l}</button>
        ))}
      </div>

      {/* 2 cartes côte à côte à partir de 1024 px ; en dessous, une par ligne (à 768 px, « Assigner » sortait de la carte). */}
      <div className="grid lg:grid-cols-2 gap-4">
        {filtered.length === 0 && <p className="text-sm text-muted-foreground">Aucune demande{filter !== "all" ? " dans ce statut" : ""}. Partagez votre lien de réservation public (/book) depuis les Réglages.</p>}
        {filtered.map((b) => {
          const st = BOOKING_STATUS[b.status] || BOOKING_STATUS.a_traiter;
          return (
            <div key={b.id} className="bg-card border border-border rounded-2xl shadow-card p-5 space-y-3" data-testid={`demande-card-${b.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-display font-bold text-lg">{b.name}{b.company ? <span className="text-muted-foreground font-normal text-sm"> · {b.company}</span> : ""}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{fmtDateTime(b.created_at)}</div>
                </div>
                <span className={cn("px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded-full border shrink-0", st.cls)} data-testid={`demande-status-${b.id}`}>{st.label}</span>
              </div>
              <div className="text-sm space-y-1.5 text-muted-foreground">
                {b.phone && <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {b.phone} {b.phone && <a href={`https://wa.me/${b.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="text-emerald-600 text-xs ml-1">WhatsApp →</a>}</div>}
                {/* Un e-mail trop long pour la ligne se coupe où il faut au lieu de sortir de la carte. */}
                {b.email && <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 shrink-0" /> <span className="min-w-0 break-words">{b.email}</span></div>}
                {b.service_type && <div className="flex items-center gap-2"><Inbox className="h-3.5 w-3.5" /> {b.service_type}</div>}
                {b.vehicle && <div className="flex items-center gap-2"><Car className="h-3.5 w-3.5" /> {b.vehicle} {b.vehicle_plate}</div>}
                {b.preferred_date && <div className="flex items-center gap-2"><Calendar className="h-3.5 w-3.5" /> {fmtDateTime(b.preferred_date)}</div>}
                {b.location && <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> {b.location}</div>}
                {b.message && <div className="flex items-start gap-2"><MessageSquare className="h-3.5 w-3.5 mt-0.5" /> <span className="italic">{b.message}</span></div>}
              </div>
              <div className="flex gap-2 pt-1 border-t border-border">
                <Button size="sm" variant="outline" className="rounded-full gap-1.5 flex-1" disabled={b.status === "contacte"} onClick={() => setStatus(b.id, "contacte")} data-testid={`demande-contact-${b.id}`}><PhoneCall className="h-3.5 w-3.5" /> Contacté</Button>
                <Button size="sm" className="rounded-full gap-1.5 flex-1" onClick={() => openAssign(b)} data-testid={`demande-assign-${b.id}`}><UserPlus className="h-3.5 w-3.5" />{limited ? ` Compléter la ${voc.mission}` : " Assigner"}</Button>
              </div>
            </div>
          );
        })}
      </div>

      <MissionDialog open={dialogOpen} onOpenChange={setDialogOpen} mission={dialogMission} onSaved={onMissionSaved} />
    </div>
  );
}